/**
 * Skeleton loading ที่ตามโครงหน้าจริง (แทนวงหมุน)
 *
 * ตอน loading(true) หน้าเดิมยังอยู่ครบ (การ์ด ตาราง ขอบ พื้นหลัง) แค่ซ่อนตัวอักษร/รูป
 * แล้ววาดกล่อง shimmer ทับตำแหน่งจริงของข้อความ ช่องกรอก ปุ่ม และรูป
 * ตารางที่ยังไม่มีข้อมูลจะวาดแถว placeholder ตามความกว้างคอลัมน์ของหัวตาราง
 * โครงหน้าเลยไม่เปลี่ยนตอนโหลดเสร็จ = ไม่กระพริบ
 *
 * ขอบเขต: modal ที่เปิดอยู่ (ถ้ามี) ไม่งั้นเนื้อหาหน้า (.body) ไม่แตะเมนูซ้าย/แถบบน
 * หน้าที่ไม่มี .body (login, verify ...) ใช้ทั้ง #root
 */

const SHOW_DELAY = 120; // ms — งานที่เสร็จไวไม่ต้องขึ้น skeleton
const MAX_BOXES = 2500;
const PLACEHOLDER_ROWS = 8;
// กล่องทั่วไปเป็น pill ให้ล้อกับปุ่ม/ช่องกรอก กล่องที่สูงเกินนี้ (รูป การ์ด) ใช้มุมมนแทน
const PILL_MAX_HEIGHT = 48;
// รูปประกอบขนาดใหญ่ (โลโก้หน้า login, ภาพประกอบ) เป็นของตายตัว ไม่ใช่ข้อมูลที่รอโหลด ปล่อยให้แสดงตามปกติ
// ถ้าซ่อนแล้ววาดกล่องทับจะได้สี่เหลี่ยมก้อนใหญ่บังหน้า
const KEEP_IMAGE_MIN_SIZE = 200;
const CONTROL_SELECTOR =
  "input:not([type=hidden]):not([type=checkbox]):not([type=radio]), select, textarea, button, .btn, img, canvas, svg, .form-control, .status-chip";

let active = false;
let timer = null;
let frame = null;
let layer = null;
let observer = null;

const getLayer = () => {
  if (layer && document.body.contains(layer)) return layer;
  layer = document.createElement("div");
  layer.id = "sk-layer";
  layer.setAttribute("aria-busy", "true");
  layer.setAttribute("aria-label", "Loading");
  document.body.appendChild(layer);
  return layer;
};

const getScopes = () => {
  // มี modal เปิดอยู่ = วาดเฉพาะ modal ตัวบนสุด (หน้าข้างหลังโดน backdrop บังอยู่แล้ว
  // ถ้าวาดด้วยจะได้กล่อง shimmer ของทั้งหน้าลอยทับ modal เต็มจอ)
  const modals = document.querySelectorAll(".modal.show");
  if (modals.length) return [modals[modals.length - 1]];
  const scopes = Array.from(document.querySelectorAll(".body"));
  if (!scopes.length) {
    const root = document.getElementById("root");
    if (root) scopes.push(root);
  }
  return scopes;
};

const isVisible = (rect) =>
  rect.width > 1 &&
  rect.height > 1 &&
  rect.bottom > 0 &&
  rect.right > 0 &&
  rect.top < window.innerHeight &&
  rect.left < window.innerWidth;

const parseColor = (value) => {
  const match = value.match(/\d+(\.\d+)?/g);
  if (!match) return null;
  const [r, g, b, a = 1] = match.map(Number);
  return { luminance: (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255, alpha: a };
};

// พื้นหลังเข้มใช้กล่องโทนเข้ม ไม่งั้นกล่องสว่างจะโดดบน hero สีกรมท่า / แผงโลโก้หน้า login
// ดูจากสีพื้นทึบของ element ที่ครอบอยู่ก่อน (แผงสีเข้มที่ตัวอักษรยังเป็นสีเข้มตาม default)
// ถ้าเจอพื้นแบบไล่สี/รูป (อ่านสีไม่ได้) หรือไม่เจอพื้นทึบเลย ค่อยเดาจากสีตัวอักษร
const toneCache = new WeakMap();
const isDarkSurface = (element) => {
  if (!element) return false;
  if (toneCache.has(element)) return toneCache.get(element);
  let dark = null;
  for (let node = element; node && node !== document.documentElement; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.backgroundImage !== "none") break;
    const background = parseColor(style.backgroundColor);
    if (background && background.alpha > 0.5) {
      dark = background.luminance < 0.4;
      break;
    }
  }
  if (dark === null) {
    const color = parseColor(getComputedStyle(element).color);
    // ตัวอักษรไล่สี (color โปร่งใส + background-clip:text) ดูสีจาก element ที่ครอบอยู่แทน
    dark = color ? (color.alpha === 0 ? isDarkSurface(element.parentElement) : color.luminance > 0.62) : false;
  }
  toneCache.set(element, dark);
  return dark;
};

// ตัดกล่องตามกรอบของ element ที่ครอบอยู่และตั้ง overflow ไว้ (การ์ดเลื่อนได้, table-responsive, text-truncate)
// ไม่งั้นเนื้อหาที่ถูกซ่อนอยู่นอกกรอบจะได้กล่อง shimmer ลอยล้นออกไปทับส่วนอื่นของหน้า
const overflowCache = new WeakMap();
let clipCache = new Map();
const getClip = (element) => {
  if (!element || element === document.body || element === document.documentElement) return null;
  if (clipCache.has(element)) return clipCache.get(element);
  let clip = getClip(element.parentElement);
  let overflow = overflowCache.get(element);
  if (!overflow) {
    const style = getComputedStyle(element);
    overflow = { x: style.overflowX !== "visible", y: style.overflowY !== "visible" };
    overflowCache.set(element, overflow);
  }
  if (overflow.x || overflow.y) {
    const own = element.getBoundingClientRect();
    clip = clip ? { ...clip } : { left: -Infinity, top: -Infinity, right: Infinity, bottom: Infinity };
    if (overflow.x) {
      clip.left = Math.max(clip.left, own.left);
      clip.right = Math.min(clip.right, own.right);
    }
    if (overflow.y) {
      clip.top = Math.max(clip.top, own.top);
      clip.bottom = Math.min(clip.bottom, own.bottom);
    }
  }
  clipCache.set(element, clip);
  return clip;
};

const pushBox = (boxes, rect, element, radius) => {
  if (boxes.length >= MAX_BOXES) return;
  const clip = getClip(element);
  if (clip) {
    const left = Math.max(rect.left, clip.left);
    const top = Math.max(rect.top, clip.top);
    const right = Math.min(rect.right, clip.right);
    const bottom = Math.min(rect.bottom, clip.bottom);
    rect = { left, top, right, bottom, width: right - left, height: bottom - top };
  }
  if (!isVisible(rect)) return;
  boxes.push({ rect, dark: isDarkSurface(element), radius });
};

const collectBoxes = () => {
  clipCache = new Map();
  const boxes = [];
  const controls = new Set();

  getScopes().forEach((scope) => {
    // ช่องกรอก ปุ่ม รูป — กล่องเท่าตัว element
    scope.querySelectorAll(CONTROL_SELECTOR).forEach((element) => {
      if (element.closest("#sk-layer") || controls.has(element)) return;
      if (element.parentElement?.closest(".btn, button, .status-chip")) return;
      controls.add(element);
      const bounds = element.getBoundingClientRect();
      if (element.tagName === "IMG" && bounds.width >= KEEP_IMAGE_MIN_SIZE && bounds.height >= KEEP_IMAGE_MIN_SIZE) {
        element.setAttribute("data-sk-keep", "");
        return;
      }
      // ใช้มุมโค้งของ element เอง กล่องจะได้ทับพอดี ไม่เห็นมุมของปุ่ม/ช่องกรอกโผล่ออกมา
      const radius = getComputedStyle(element).borderRadius;
      pushBox(boxes, bounds, element.parentElement || element, radius !== "0px" ? radius : "");
    });

    // ข้อความ — กล่องตามบรรทัดจริงของตัวอักษร
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.nodeValue.trim() &&
        !node.parentElement?.closest("#sk-layer, .btn, button, .status-chip, script, style")
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT,
    });
    const range = document.createRange();
    let node = walker.nextNode();
    while (node && boxes.length < MAX_BOXES) {
      const parent = node.parentElement;
      // ตัวอักษรไล่สี (background-clip:text) ซ่อนด้วย text-fill-color ไม่ได้ ต้องซ่อนทั้ง element (ยังกินที่เท่าเดิม)
      if (parent && !parent.hasAttribute("data-sk-hide") && getComputedStyle(parent).webkitBackgroundClip === "text") {
        parent.setAttribute("data-sk-hide", "");
      }
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) pushBox(boxes, rect, parent);
      node = walker.nextNode();
    }

    // ตารางที่ยังไม่มีข้อมูล — แถว placeholder ตามคอลัมน์หัวตาราง
    scope.querySelectorAll("table").forEach((table) => {
      const rows = table.tBodies[0]?.rows || [];
      const headRow = table.tHead?.rows[table.tHead.rows.length - 1];
      const empty = rows.length === 0 || (rows.length === 1 && rows[0].cells.length === 1 && rows[0].cells[0].colSpan > 1);
      if (!headRow || !empty) return;
      const head = headRow.getBoundingClientRect();
      if (!isVisible(head)) return;
      const cells = Array.from(headRow.cells).map((cell) => cell.getBoundingClientRect());
      for (let row = 0; row < PLACEHOLDER_ROWS; row++) {
        const top = head.bottom + row * 52 + 19;
        if (top > window.innerHeight) break;
        cells.forEach((cell, col) => {
          const width = Math.max(18, (cell.width - 24) * (0.55 + (((row + col) % 3) * 0.15)));
          pushBox(boxes, { left: cell.left + 12, top, width, height: 14, right: cell.left + 12 + width, bottom: top + 14 }, table);
        });
      }
    });
  });
  return boxes;
};

const draw = () => {
  frame = null;
  if (!active) return;
  const html = collectBoxes()
    .map(
      ({ rect, dark, radius }) =>
        `<div class="sk-box${dark ? " sk-dark" : ""}${rect.height > PILL_MAX_HEIGHT ? " sk-large" : ""}" style="left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px${radius ? `;border-radius:${radius}` : ""}"></div>`,
    )
    .join("");
  const target = getLayer();
  // วาดเฉพาะเนื้อหาหน้า (.body) = ให้ layer อยู่ใต้แถบบน/เมนูซ้าย (z-index 1020/1021) กล่องที่เลื่อนขึ้นไปจะไม่ลอยทับเมนู
  target.classList.toggle("sk-page", !document.querySelector(".modal.show") && Boolean(document.querySelector(".body")));
  target.innerHTML = html;
};

const schedule = () => {
  if (active && !frame) frame = requestAnimationFrame(draw);
};

const onMutation = (mutations) => {
  if (mutations.every((mutation) => layer && layer.contains(mutation.target))) return;
  schedule();
};

const start = () => {
  timer = null;
  if (active) return;
  active = true;
  document.body.classList.add("sk-active");
  getLayer().classList.add("show");
  observer = observer || new MutationObserver(onMutation);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  window.addEventListener("scroll", schedule, true);
  window.addEventListener("resize", schedule);
  schedule();
};

const stop = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!active) return;
  active = false;
  observer?.disconnect();
  window.removeEventListener("scroll", schedule, true);
  window.removeEventListener("resize", schedule);
  if (frame) cancelAnimationFrame(frame);
  frame = null;
  document.body.classList.remove("sk-active");
  document.querySelectorAll("[data-sk-hide]").forEach((element) => element.removeAttribute("data-sk-hide"));
  document.querySelectorAll("[data-sk-keep]").forEach((element) => element.removeAttribute("data-sk-keep"));
  if (layer) {
    layer.classList.remove("show");
    layer.innerHTML = "";
  }
};

const skeleton = (status, immediate = false) => {
  if (status) {
    if (active || timer) return;
    if (immediate) start();
    else timer = setTimeout(start, SHOW_DELAY);
  } else {
    stop();
  }
};

export default skeleton;
